import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  deleteKnowledgeDocument,
  getHouseSeedStatus,
  getKnowledgeDocumentContent,
  ingestKnowledgeDocument,
  listKnowledgeDocuments,
  removeHouseMethodologySeed,
  seedHouseMethodology,
  setKnowledgeLifecycle,
  setKnowledgeQuality,
  setKnowledgeReviewDate,
  type KnowledgeDocument,
  type KnowledgeLifecycle,
  type KnowledgeScope,
} from '@/lib/admin/knowledge-api';


type Props = {
  scope: KnowledgeScope;
  orgId?: string;
  title: string;
  description: string;
};

const LIFECYCLE_LABEL: Record<KnowledgeLifecycle, string> = {
  draft: 'Draft',
  approved: 'Approved',
  archived: 'Archived',
};

export function KnowledgeManager({ scope, orgId, title, description }: Props) {
  const qc = useQueryClient();
  const queryKey = ['knowledge', scope, orgId ?? 'house'];

  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [docTitle, setDocTitle] = useState('');
  const [tags, setTags] = useState('');
  const [content, setContent] = useState('');


  const docs = useQuery({
    queryKey,
    queryFn: () => listKnowledgeDocuments(scope, orgId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const reset = () => {
    setDocTitle('');
    setTags('');
    setContent('');
    setAdding(false);
  };

  const ingest = useMutation({
    mutationFn: () =>
      ingestKnowledgeDocument({
        scope,
        orgId: orgId ?? null,
        title: docTitle.trim(),
        content,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      }),
    onSuccess: (r) => {
      toast({
        title: 'Document indexed as draft',
        description: `${r.chunks} passages indexed. Approve it before Ask can use it.`,
      });
      reset();
      invalidate();
    },
    onError: (e: Error) =>
      toast({ title: 'Could not index document', description: e.message, variant: 'destructive' }),
  });

  const lifecycle = useMutation({
    mutationFn: (v: { id: string; status: KnowledgeLifecycle }) =>
      setKnowledgeLifecycle(v.id, v.status),
    onSuccess: invalidate,
    onError: (e: Error) =>
      toast({ title: 'Could not update status', description: e.message, variant: 'destructive' }),
  });

  const quality = useMutation({
    mutationFn: (v: { id: string; score: number }) => setKnowledgeQuality(v.id, v.score),
    onSuccess: invalidate,
    onError: (e: Error) =>
      toast({ title: 'Could not update rating', description: e.message, variant: 'destructive' }),
  });

  const review = useMutation({
    mutationFn: (v: { id: string; date: string | null }) => setKnowledgeReviewDate(v.id, v.date),
    onSuccess: invalidate,
    onError: (e: Error) =>
      toast({ title: 'Could not set review date', description: e.message, variant: 'destructive' }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteKnowledgeDocument(id),
    onSuccess: () => {
      toast({ title: 'Document removed', description: 'Its passages are out of retrieval.' });
      invalidate();
    },
    onError: (e: Error) =>
      toast({ title: 'Could not delete', description: e.message, variant: 'destructive' }),
  });

  const onFile = async (file: File) => {
    const text = await file.text();
    setContent(text);
    if (!docTitle) setDocTitle(file.name.replace(/\.[^.]+$/, ''));
  };

  const seedStatus = useQuery({
    queryKey: ['house-seed-status'],
    queryFn: getHouseSeedStatus,
    enabled: scope === 'house',
    retry: false,
  });

  const afterSeedChange = () => {
    invalidate();
    void seedStatus.refetch();
  };

  const seed = useMutation({
    mutationFn: () => seedHouseMethodology(true),
    onSuccess: (r) => {
      toast({ title: 'House methodology seeded', description: r.note });
      afterSeedChange();
    },
    onError: (e: Error) =>
      toast({ title: 'Could not seed', description: e.message, variant: 'destructive' }),
  });

  const unseed = useMutation({
    mutationFn: removeHouseMethodologySeed,
    onSuccess: (r) => {
      toast({
        title: 'Seed removed',
        description: `${r.removed} seeded documents and their passages deleted.`,
      });
      afterSeedChange();
    },
    onError: (e: Error) =>
      toast({ title: 'Could not remove seed', description: e.message, variant: 'destructive' }),
  });



  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <CardTitle className="text-base font-heading">{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          New uploads land as drafts. Only approved, current documents are retrieved by Ask.
          Re-uploading the same title creates a new version and archives the old one, so answers
          never see two versions of the same guidance.
        </p>

        {scope === 'house' && seedStatus.data && (
          <div className="rounded-md border border-border p-4 space-y-2">
            <p className="text-sm font-medium">Starter house methodology</p>
            <p className="text-xs text-muted-foreground">
              Seven curated playbooks: change, psychological safety, workload, recognition,
              growth, collaboration and direction. {seedStatus.data.seeded} of{' '}
              {seedStatus.data.available} currently in the library. Everything seeded is tagged
              and removable in one action.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                size="sm"
                onClick={() => seed.mutate()}
                disabled={seed.isPending || unseed.isPending}
              >
                {seed.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {seedStatus.data.seeded > 0 ? 'Re-seed house methodology' : 'Seed house methodology'}
              </Button>
              {seedStatus.data.seeded > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => unseed.mutate()}
                  disabled={seed.isPending || unseed.isPending}
                >
                  {unseed.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Remove seeded set
                </Button>
              )}
            </div>
          </div>
        )}

        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add document
          </Button>
        )}

        {adding && (
          <form
            className="space-y-3 border border-border rounded-md p-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (docTitle.trim().length < 2 || content.trim().length < 20) return;
              ingest.mutate();
            }}
          >
            <Input
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              placeholder="Title, for example: Retention playbook"
              maxLength={200}
              disabled={ingest.isPending}
            />
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Tags, comma separated (optional)"
              disabled={ingest.isPending}
            />
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste the text, or load a .txt / .md file below."
              rows={10}
              disabled={ingest.isPending}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-xs text-muted-foreground flex items-center gap-2 cursor-pointer">
                <Upload className="h-3.5 w-3.5" />
                <span>Load .txt or .md</span>
                <input
                  type="file"
                  accept=".txt,.md,text/plain,text/markdown"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void onFile(f);
                  }}
                />
              </label>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={reset} disabled={ingest.isPending}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={ingest.isPending || docTitle.trim().length < 2 || content.trim().length < 20}
                >
                  {ingest.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Index document
                </Button>
              </div>
            </div>
          </form>
        )}

        {docs.isLoading && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading documents…
          </p>
        )}

        {docs.data && docs.data.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground">
            No documents yet. Anything approved here is retrieved when Ask writes recommendations.
          </p>
        )}

        {docs.data && docs.data.length > 0 && (
          <ul className="divide-y divide-border border border-border rounded-md">
            {docs.data.map((d) => {
              const retrievable =
                d.status === 'ready' &&
                d.lifecycle_status === 'approved' &&
                !d.superseded_by &&
                (!d.review_at || d.review_at >= new Date().toISOString().slice(0, 10));
              return (
                <li key={d.id} className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="text-sm font-medium truncate flex items-center gap-1.5 text-left hover:underline"
                        aria-expanded={openId === d.id}
                        onClick={() => setOpenId(openId === d.id ? null : d.id)}
                      >
                        {openId === d.id ? (
                          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                        )}
                        <span className="truncate">{d.title}</span>
                        <span className="text-xs text-muted-foreground font-normal">v{d.version}</span>
                      </button>

                      <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-1.5">
                        <Badge
                          variant={
                            d.status === 'ready'
                              ? 'outline'
                              : d.status === 'failed'
                                ? 'destructive'
                                : 'secondary'
                          }
                          className="text-[10px]"
                        >
                          {d.status === 'ready' ? `${d.chunk_count} passages` : d.status}
                        </Badge>
                        <Badge
                          variant={retrievable ? 'default' : 'secondary'}
                          className="text-[10px]"
                        >
                          {retrievable ? 'In retrieval' : 'Not retrieved'}
                        </Badge>
                        {d.superseded_by && (
                          <Badge variant="secondary" className="text-[10px]">
                            Superseded
                          </Badge>
                        )}
                        <span>{new Date(d.created_at).toLocaleDateString()}</span>
                        {d.tags.map((t) => (
                          <Badge key={t} variant="secondary" className="text-[10px]">
                            {t}
                          </Badge>
                        ))}
                      </div>
                      {d.error_message && (
                        <p className="text-xs text-destructive mt-1">{d.error_message}</p>
                      )}
                      {d.ingest_warnings.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {d.ingest_warnings.map((w, i) => (
                            <li
                              key={i}
                              className="text-xs text-muted-foreground flex items-start gap-1.5"
                            >
                              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-primary" />
                              <span>{w}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${d.title}`}
                      onClick={() => remove.mutate(d.id)}
                      disabled={remove.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={d.lifecycle_status}
                      onValueChange={(v) =>
                        lifecycle.mutate({ id: d.id, status: v as KnowledgeLifecycle })
                      }
                    >
                      <SelectTrigger className="h-8 w-[130px] text-xs" aria-label="Review status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(['draft', 'approved', 'archived'] as KnowledgeLifecycle[]).map((s) => (
                          <SelectItem key={s} value={s} className="text-xs">
                            {LIFECYCLE_LABEL[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={String(d.quality_score)}
                      onValueChange={(v) => quality.mutate({ id: d.id, score: Number(v) })}
                    >
                      <SelectTrigger className="h-8 w-[140px] text-xs" aria-label="Quality rating">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <SelectItem key={n} value={String(n)} className="text-xs">
                            Quality {n} of 5
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                      Review by
                      <Input
                        type="date"
                        className="h-8 w-[150px] text-xs"
                        value={d.review_at ?? ''}
                        onChange={(e) =>
                          review.mutate({ id: d.id, date: e.target.value || null })
                        }
                      />
                    </label>
                  </div>

                  {openId === d.id && (
                    <DocumentEditor
                      doc={d}
                      scope={scope}
                      orgId={orgId}
                      onClose={() => setOpenId(null)}
                      onSaved={invalidate}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function DocumentEditor({
  doc,
  scope,
  orgId,
  onClose,
  onSaved,
}: {
  doc: KnowledgeDocument;
  scope: KnowledgeScope;
  orgId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(doc.title);
  const [tags, setTags] = useState(doc.tags.join(', '));
  const [text, setText] = useState('');

  const body = useQuery({
    queryKey: ['knowledge-content', doc.id],
    queryFn: () => getKnowledgeDocumentContent(doc.id),
  });

  useEffect(() => {
    if (body.data != null) setText(body.data);
  }, [body.data]);

  const save = useMutation({
    mutationFn: () =>
      ingestKnowledgeDocument({
        scope,
        orgId: orgId ?? null,
        title: title.trim(),
        content: text,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        documentId: doc.id,
      }),
    onSuccess: (r) => {
      toast({
        title: `Saved as v${r.version ?? doc.version + 1}`,
        description: `${r.chunks} passages re-indexed. Approve it again before Ask uses it.`,
      });
      setEditing(false);
      onSaved();
      onClose();
    },
    onError: (e: Error) =>
      toast({ title: 'Could not save changes', description: e.message, variant: 'destructive' }),
  });

  if (body.isLoading) {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-2 pt-1">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading document…
      </p>
    );
  }

  if (body.error) {
    return <p className="text-xs text-destructive pt-1">{(body.error as Error).message}</p>;
  }

  if (!editing) {
    return (
      <div className="space-y-2 pt-1">
        <div className="rounded-md border border-border bg-muted/30 p-3 max-h-80 overflow-y-auto">
          <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">
            {text || 'This document has no stored text.'}
          </pre>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="h-3.5 w-3.5 mr-2" />
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  const valid = title.trim().length >= 2 && text.trim().length >= 20;

  return (
    <form
      className="space-y-3 pt-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        placeholder="Title"
        disabled={save.isPending}
      />
      <Input
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="Tags, comma separated"
        disabled={save.isPending}
      />
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={16}
        className="font-mono text-xs"
        disabled={save.isPending}
      />
      <p className="text-xs text-muted-foreground">
        Saving re-indexes the whole document as a new version and returns it to draft, so Ask keeps
        using the current text until you approve the update.
      </p>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending || !valid}>
          {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save and re-index
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            setTitle(doc.title);
            setTags(doc.tags.join(', '));
            setText(body.data ?? '');
            setEditing(false);
          }}
          disabled={save.isPending}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

